/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
import { arrowUpRight as Arrow } from "../../../assets/styledSvg/arrowUpRight";
import arrowDown from "../../../assets/svg/arrow-down.svg";
import payment from "../../../assets/svg/dollar.svg";
import {
  CollateralAssetResponse,
  NotificationTypeResponse,
} from "../../../codegen-api";
import {
  COLORS,
  PRIMARY_COLORS,
  TEXT_COLORS,
} from "../../../constants/design/colors";
import { getRewardDetails } from "../../../constants/rewards";
import {
  getAssetShortName,
  getCollateralAssetByAddress,
} from "../../../utils/asset/assets";
import { getTimeAgo } from "../../../utils/date";
import { shortenAddress } from "../../../utils/strings";
import TickerLogo from "../TickerLogo";
import {
  ActionBadge,
  Content,
  Icon,
  IconWrapper,
  NotificationWrapper,
  ReadIndicator,
  StaticExternalLinkWrapper,
  Timestamp,
  Title,
} from "./style";
import {
  activeUsdcAddress,
  activityMoment,
  formatCollateralAmount,
} from "./helpers";
import { INotificationProps } from "./types";

export function DepositReceiveNotification({
  notification,
  t,
  translateAll,
}: INotificationProps) {
  const amount = notification.metadata?.amount || "0";
  const collateralAddress = notification.metadata?.collateral;
  const collateral = collateralAddress
    ? getCollateralAssetByAddress(collateralAddress)
    : undefined;
  // Prefer the symbol the backend resolved (works for vault rows that have no
  // currency address); fall back to the address-derived name for old rows.
  const assetSymbol =
    notification.metadata?.asset || getAssetShortName(collateral) || undefined;
  const formattedAmount = formatCollateralAmount(amount, collateralAddress);
  const formattedCollateralAmount = formattedAmount
    ? `${formattedAmount} ${getAssetShortName(collateral) || assetSymbol || ""}`
    : getAssetShortName(collateral) || assetSymbol || "";

  let title: string | JSX.Element = t("deposit_complete");
  let subtitle: string | JSX.Element = t("deposit_complete_desc", {
    asset: formattedCollateralAmount,
  });

  const rewardsDetails = getRewardDetails({
    type: "notification",
    notificationOrTransaction: notification,
  });

  if (notification.notification_type === NotificationTypeResponse.Receive) {
    title = t("transfer_received");

    subtitle = t("transfer_received_desc", {
      asset: formattedCollateralAmount,
      address: shortenAddress(notification.metadata?.account || ""),
    });

    if (rewardsDetails) {
      const { link, programName } = rewardsDetails;
      title = t("program_received", { programName });
      if (rewardsDetails.link) {
        subtitle = (
          <span>
            {t("program_received_desc_1", {
              asset: formattedCollateralAmount,
              programName,
            })}
            <StaticExternalLinkWrapper
              href={link}
              target="_blank"
              rel="noreferrer"
            >
              {t("program_received_desc_2")} <Arrow />
            </StaticExternalLinkWrapper>
          </span>
        );
      } else if (notification.metadata?.label === "AUTOMATIC_CREDIT") {
        title = t("trading_credits_received");
        subtitle = (
          <span>
            {t("trading_credits_received_desc", {
              asset: formattedCollateralAmount,
            })}
          </span>
        );
      } else {
        subtitle = t("default_received_desc", {
          asset: formattedCollateralAmount,
          programName,
        });
      }
    }
  }

  return (
    <NotificationWrapper isRead={notification.is_read}>
      {rewardsDetails ? (
        <IconWrapper backgroundColor={COLORS.highlight.five}>
          <Icon src={payment} />
          {!notification.is_read && <ReadIndicator />}
        </IconWrapper>
      ) : (
        // Token logo with an "incoming" action badge so the asset (logo) and the
        // action (deposit/receive) are both visible at a glance.
        <IconWrapper>
          <TickerLogo asset={assetSymbol} size={32} />
          <ActionBadge backgroundColor={COLORS.positive.one}>
            <img src={arrowDown} alt="" />
          </ActionBadge>
          {!notification.is_read && <ReadIndicator />}
        </IconWrapper>
      )}
      <div>
        <Title>{title}</Title>
        <Content $color={TEXT_COLORS.two}>{subtitle}</Content>
        <Timestamp>
          {getTimeAgo(activityMoment(notification), translateAll)}
        </Timestamp>
      </div>
    </NotificationWrapper>
  );
}

export function WithdrawalNotification({
  notification,
  t,
  translateAll,
}: INotificationProps) {
  const amount = notification.metadata?.amount || "0";
  const collateralAddress =
    notification.metadata?.collateral || notification.metadata?.l1Token;
  const collateral = collateralAddress
    ? getCollateralAssetByAddress(collateralAddress)
    : undefined;
  const assetSymbol =
    notification.metadata?.asset || getAssetShortName(collateral) || undefined;
  const formattedAmount = formatCollateralAmount(amount, collateralAddress);
  const formattedCollateralAmount = formattedAmount
    ? `${formattedAmount} ${getAssetShortName(collateral) || assetSymbol || ""}`
    : getAssetShortName(collateral) || assetSymbol || "";

  return (
    <NotificationWrapper isRead={notification.is_read}>
      {/* Token logo with an "outgoing" action badge (up arrow). */}
      <IconWrapper>
        <TickerLogo asset={assetSymbol} size={32} />
        <ActionBadge backgroundColor={COLORS.negative.one}>
          <img src={arrowDown} alt="" style={{ transform: "rotate(180deg)" }} />
        </ActionBadge>
        {!notification.is_read && <ReadIndicator />}
      </IconWrapper>
      <div>
        <Title>{t("withdrawal_complete")}</Title>
        <Content $color={TEXT_COLORS.two}>
          {t("withdrawal_complete_desc", { asset: formattedCollateralAmount })}
        </Content>
        <Timestamp>
          {getTimeAgo(activityMoment(notification), translateAll)}
        </Timestamp>
      </div>
    </NotificationWrapper>
  );
}

export function SendFinalizedNotification({
  notification,
  t,
  translateAll,
}: INotificationProps) {
  const amount = notification.metadata?.amount || "0";
  const collateral = CollateralAssetResponse.Usdc;
  const label = notification.metadata?.label;
  const formattedAmount = formatCollateralAmount(amount, activeUsdcAddress());
  const formattedCollateralAmount = formattedAmount
    ? `${formattedAmount} ${getAssetShortName(collateral)}`
    : getAssetShortName(collateral) ?? "";
  const isVaultDeposit = label === "YV_DEPOSIT";
  const isVaultRedemption = label === "YV_WITHDRAW" || label === "YV_REDEEM";

  const title: string | JSX.Element =
    isVaultDeposit
      ? t("vault_deposit_complete")
      : isVaultRedemption
      ? t("vault_redemption_complete")
      : t("transfer_sent");
  const subtitle: string | JSX.Element =
    isVaultDeposit
      ? t("vault_deposit_complete_desc", {
          amount: formattedCollateralAmount,
        })
      : isVaultRedemption
      ? t("vault_redemption_complete_desc", {
          amount: formattedCollateralAmount,
        })
      : t("transfer_sent_desc", {
          asset: formattedCollateralAmount,
          address: shortenAddress(notification.metadata?.account || ""),
        });

  return (
    <NotificationWrapper isRead={notification.is_read}>
      <IconWrapper backgroundColor={PRIMARY_COLORS.one}>
        <Icon
          src={arrowDown}
          // The arrow asset is white; on the bright cyan accent fill it needs to
          // read dark for contrast. It's an <img>, so darken it via filter
          // rather than `color` (≈ PRIMARY_COLORS.on).
          style={{
            transform: isVaultDeposit ? "" : "rotate(180deg)",
            filter: "brightness(0)",
          }}
        />
        {!notification.is_read && <ReadIndicator />}
      </IconWrapper>
      <div>
        <Title>{title}</Title>
        <Content $color={TEXT_COLORS.two}>{subtitle}</Content>
        <Timestamp>
          {getTimeAgo(activityMoment(notification), translateAll)}
        </Timestamp>
      </div>
    </NotificationWrapper>
  );
}
